import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["addressSelect", "addressFields", "companyAddressNotice"];

  connect() {
    this.initializeState();
  }

  initializeState() {
    // Initialize form fields based on current dropdown selection
    this.handleAddressSelection({ target: this.addressSelectTarget });
  }

  handleAddressSelection(event) {
    const selectedValue = event.target.value;
    const isCompanyAddress = selectedValue === "company_address";

    if (isCompanyAddress) {
      this.selectCompanyAddress();
    } else if (selectedValue === "new_contact") {
      // Clear form fields for new contact entry and pre-fill company name as default.
      this.clearAddressFields();
      this.prefillCompanyName();
      this.toggleFieldsDisabled(false);
      this.showCompanyAddressNotice(false);
      this.focusFirstNameField();
    } else {
      try {
        const contactData = JSON.parse(selectedValue);
        this.populateAddressFields(contactData);
        this.toggleFieldsDisabled(false);
        this.showCompanyAddressNotice(false);
      } catch (error) {
        console.error("Error parsing contact data:", error);
        this.selectCompanyAddress();
      }
    }
  }

  selectCompanyAddress() {
    this.loadCompanyAddress();
    this.toggleFieldsDisabled(true);
    this.showCompanyAddressNotice(true);
  }

  loadCompanyAddress() {
    const addressFields = this.addressFieldsTarget;

    if (!addressFields) {
      console.error("Address fields target not found");
      return;
    }

    const companyData = {
      company_name: addressFields.dataset.companyName || "",
      first_name: addressFields.dataset.companyFirstname || "",
      last_name: addressFields.dataset.companyLastname || "",
      email: addressFields.dataset.companyEmail || "",
      street: addressFields.dataset.companyStreet || "",
      city: addressFields.dataset.companyCity || "",
      zipcode: addressFields.dataset.companyZipcode || "",
      country: addressFields.dataset.companyCountry || "DE",
    };

    const fieldMappings = {
      group_invoice_form_invoice_firstname: companyData.first_name,
      group_invoice_form_invoice_lastname: companyData.last_name,
      group_invoice_form_invoice_company_name: companyData.company_name,
      group_invoice_form_invoice_email: companyData.email,
      group_invoice_form_invoice_street: companyData.street,
      group_invoice_form_invoice_city: companyData.city,
      group_invoice_form_invoice_zipcode: companyData.zipcode,
      group_invoice_form_invoice_country_code: companyData.country,
    };

    // Populate form fields with company data
    Object.entries(fieldMappings).forEach(([fieldId, value]) => {
      const field = document.getElementById(fieldId);
      if (field) {
        if (field.tagName.toLowerCase() === "select") {
          // Handle select fields (like country dropdown)
          field.value = value;
          // Trigger change event for any listeners
          field.dispatchEvent(new Event("change", { bubbles: true }));
        } else {
          // Handle regular input fields
          field.value = value;
        }
      }
    });
  }

  clearAddressFields() {
    const fields = this.getAddressFieldElements();
    fields.forEach((field) => {
      if (field.type === "text" || field.type === "email") {
        field.value = "";
      }
    });
  }

  prefillCompanyName() {
    const addressFields = this.addressFieldsTarget;
    const companyNameField = document.getElementById(
      "group_invoice_form_invoice_company_name"
    );

    if (companyNameField && addressFields) {
      const companyName = addressFields.dataset.companyName || "";
      companyNameField.value = companyName;
    }
  }

  populateAddressFields(contactData) {
    // Map contact data to form fields
    const fieldMappings = {
      group_invoice_form_invoice_firstname: contactData.first_name || "",
      group_invoice_form_invoice_lastname: contactData.last_name || "",
      group_invoice_form_invoice_company_name: contactData.company_name || "",
      group_invoice_form_invoice_email:
        (contactData.emails && contactData.emails[0]) || "",
      group_invoice_form_invoice_street: contactData.street || "",
      group_invoice_form_invoice_city: contactData.city || "",
      group_invoice_form_invoice_zipcode: contactData.zip_code || "",
      group_invoice_form_invoice_country_code: contactData.country || "DE",
    };

    Object.entries(fieldMappings).forEach(([fieldId, value]) => {
      const field = document.getElementById(fieldId);
      if (field) {
        field.value = value;
      }
    });
  }

  getAddressFieldElements() {
    const fieldIds = [
      "group_invoice_form_invoice_firstname",
      "group_invoice_form_invoice_lastname",
      "group_invoice_form_invoice_company_name",
      "group_invoice_form_invoice_email",
      "group_invoice_form_invoice_street",
      "group_invoice_form_invoice_city",
      "group_invoice_form_invoice_zipcode",
    ];

    return fieldIds
      .map((id) => document.getElementById(id))
      .filter((el) => el !== null);
  }

  toggleFieldsDisabled(disabled) {
    const fields = this.getAddressFieldElements();

    // Also include the country select field
    const countryField = document.getElementById(
      "group_invoice_form_invoice_country_code"
    );
    if (countryField) {
      fields.push(countryField);
    }

    fields.forEach((field) => {
      if (field) {
        // Use readonly instead of disabled to prevent editing while allowing form submission
        field.readOnly = disabled;
        if (disabled) {
          field.classList.add("disabled");
          field.style.backgroundColor = "#f8f9fa";
          field.style.cursor = "not-allowed";
          // Prevent focus and text selection for better UX
          field.tabIndex = -1;
        } else {
          field.classList.remove("disabled");
          field.style.backgroundColor = "";
          field.style.cursor = "";
          field.tabIndex = 0;
        }
      }
    });
  }

  showCompanyAddressNotice(show) {
    if (this.hasCompanyAddressNoticeTarget) {
      if (show) {
        this.companyAddressNoticeTarget.classList.remove("d-none");
      } else {
        this.companyAddressNoticeTarget.classList.add("d-none");
      }
    }
  }

  focusFirstNameField() {
    const firstNameField = document.getElementById(
      "group_invoice_form_invoice_firstname"
    );
    if (firstNameField) {
      // Use setTimeout to ensure the field is enabled and ready before focusing
      setTimeout(() => {
        firstNameField.focus();
      }, 50);
    }
  }
}
